import React, { useState, useRef } from "react";
import ActionBar from "../../common/ActionBar";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import CustomTable from "../../common/CustomTable/CustomTable";
import { navigate } from "@reach/router";
import _ from "lodash";
import moment from "moment";
import FilterModal from "./modal";
import queryString from "query-string";
import CaseService from "../../../services/CaseService";
import { useEffect } from "react";
import {
  numberFormat,
  getCaseLabelByStatus,
  getErrorMessage,
  parseTimeStamp,
} from "../../../helpers/functions";
import labels from "../../../helpers/labels.json";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AlertDialog from "../../common/Alert";
import { PrimaryCTAButton } from "../../common/Buttons";
import usePrevious from "../../../hooks/usePrevious";
import AssignNeutralBulkCase from "../../pages/AssignNeutralBulkCase/index";
import AssignCaseLifeCycleBulkCase from "../../pages/AssignCaseLifeCycleBulkCase/index";
import BulkUpload from "../BulkUpload";
import useUser from "../../../hooks/useUser";

const STATUS_PALETTE = [
  { bg: "#eff6ff", accent: "#3b82f6", text: "#1e40af" }, // blue
  { bg: "#f0fdfa", accent: "#14b8a6", text: "#0f766e" }, // teal
  { bg: "#fdf4ff", accent: "#c026d3", text: "#86198f" }, // fuchsia
  { bg: "#fff7ed", accent: "#fb923c", text: "#9a3412" }, // orange
  { bg: "#f5f3ff", accent: "#8b5cf6", text: "#5b21b6" }, // violet
  { bg: "#ecfeff", accent: "#06b6d4", text: "#155e75" }, // cyan
  { bg: "#fefce8", accent: "#eab308", text: "#854d0e" }, // yellow
  { bg: "#fdf2f8", accent: "#ec4899", text: "#9d174d" }, // pink
];

const hashKeyToIndex = (str, length) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % length;
};

const getStatusColor = (key = "") => {
  const k = key.toLowerCase();
  if (k.includes("draft"))
    return { bg: "#f1f5f9", accent: "#94a3b8", text: "#475569" };
  if (k.includes("settled") || k.includes("approved") || k.includes("closed"))
    return { bg: "#f0fdf4", accent: "#22c55e", text: "#15803d" };
  if (k.includes("reject") || k.includes("cancel") || k.includes("withdraw"))
    return { bg: "#fef2f2", accent: "#ef4444", text: "#b91c1c" };
  if (k.includes("pending") || k.includes("fee") || k.includes("waiting"))
    return { bg: "#fffbeb", accent: "#f59e0b", text: "#92400e" };
  return STATUS_PALETTE[hashKeyToIndex(key, STATUS_PALETTE.length)];
};

const initFilters = [
  { label: "All", key: "allCount", value: 0 },
  {
    label: "Waiting For Respondent",
    key: "awaitingRespondentOnBoardingCount",
    value: 0,
  },
  { label: "Under Resolution", key: "underResolution", value: 0 },
  { label: "Settled", key: "settled", value: 0 },
  { label: "Drafts", key: "draftCount", value: 0 },
];

const filterByStatus = (selectedFilter) => {
  let key = "status";
  let value = "";
  switch (selectedFilter) {
    case "Drafts":
      value = "draft";
      break;
    case "Under Resolution":
      value = "underResolution";
      break;
    case "Waiting For Respondent":
      value = "awaitingRespondent";
      break;
    case "Settled":
      value = "settled";
      break;
    default:
      break;
  }
  return { key, value };
};

async function getCases(query = "", type) {
  try {
    const response = await CaseService.index(query);
    const stats = {
      mediationCount: response?.mediationCount,
      negotiationCount: response?.negotiationCount,
      arbitrationCount: response?.arbitrationCount,
      draftCount: response?.draftCount,
      allCount: response?.allCount,
      activeCount: response?.activeCount,
      underResolution: response?.underResolution,
      settled: response?.settled,
      awaitingRespondentOnBoardingCount:
        response?.awaitingRespondentOnBoardingCount,
    };
    return { ...response, stats };
  } catch (error) {
    return error;
  }
}

const Index = ({ type }) => {
  const [state, setState] = useState({});
  const [filters, setFilters] = useState(initFilters);
  const placeholderText = "Search Cases";
  const [selectedFilter, setSelectedFilter] = useState(filters[0].label);
  const [modal, setModal] = useState();
  const filterPreviousValue = usePrevious(filters);
  const MTRef = useRef();
  const breadcrumbs = [_.startCase(labels.cases), _.startCase(type)];
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [lifecycleModal, setLifeCycleModal] = useState({ state: false });
  const [bulkUpload, setBulkUpload] = useState(false);
  const [cmList, setCmList] = useState([]);
  const { userObject } = useUser();

  function setStats(stats) {
    const mapped = initFilters.map((filter) => {
      let returnData = {
        label: filter.label,
        value: filter.value,
      };
      if (stats[filter.key] >= 0) {
        returnData.value = stats[filter.key];
      }
      return returnData;
    });
    setFilters(mapped);
  }

  useEffect(() => {
    if (selectedFilter && filterPreviousValue) {
      MTRef.current.onQueryChange({
        ...MTRef.current.state.query,
        page: 0,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    async function getCMlist(data) {
      try {
        setLoader({ state: true, message: `Fetching...` });
        let params = {
          page: 1,
          perPage: 1000,
          role: "caseManager",
        };
        let stringParams = "";
        if (!_.isEmpty(params)) {
          stringParams = `?${queryString.stringify(params)}`;
        }
        const response = await CaseService.getCMlist(stringParams);
        if (response?.data?.length) {
          setCmList(
            response?.data?.map(({ name, email, id }) => ({
              label: (
                <Container>
                  <div className="agent_row">
                    <div>
                      <div className="agent_name">{name}</div>
                      <div className="agent_email">({email})</div>
                    </div>
                  </div>
                </Container>
              ),
              value: id,
            })),
          );
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
    getCMlist(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getMeetingjoin(id) {
    try {
      const meetingurl = await CaseService.getMeeting(id);
      window.open(meetingurl);
      return;
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    }
  }

  const actions = [
    {
      text: "Assign View Only User",
      props: {
        onClick: () => {
          setDrawer({ state: true, assignType: "viewOnly" });
        },
      },
      hidden: type !== "negotiation" ? false : true,
    },
    {
      text: "Assign Neutral",
      props: {
        onClick: () => {
          setDrawer({ state: true, assignType: "neutral" });
        },
      },
      hidden: type !== "negotiation" ? false : true,
    },
    {
      text: "Case Status",
      props: {
        onClick: () => setLifeCycleModal({ state: true }),
      },
      hidden: type === "arbitration" ? false : true,
    },
    {
      text: "Bulk Upload",
      props: {
        onClick: () => setBulkUpload(true),
      },
    },
  ];

  const columns = [
    {
      field: "id",
      title: "Case ID",
      sorting: false,
      render: (rowData) => (
        <HyperLink
          onClick={() =>
            navigate(
              `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
            )
          }
        >
          {rowData.id}
        </HyperLink>
      ),
      tooltip: "Unique Identifier for the Case across JustAct platform",
      headerStyle: {
        paddingLeft: "26px",
        padding: `12px 16px`,
      },
      cellStyle: {
        maxWidth: 100,
        width: 100,
        padding: `12px 16px`,
      },
    },
    {
      field: "title",
      title: "Case Title",
      render: (rowData) => (
        <Bold
          onClick={() =>
            navigate(
              `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
            )
          }
        >
          {rowData.title}
        </Bold>
      ),
      headerStyle: {
        width: "18%",
      },
      sorting: false,
    },
    {
      field: "party",
      title: "Party",
      render: (rowData) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate(
              `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
            )
          }
        >
          {rowData?.claimantParty?.name}
        </div>
      ),
      tooltip: "Parties representing to file the case",
      sorting: false,
    },
    {
      field: "respondentName",
      title: "Counterparty",
      tooltip: "Party countering your case",
      sorting: false,
      render: (rowData) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate(
              `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
            )
          }
        >
          {rowData?.respondentParty?.name ? (
            rowData.respondentParty.name
          ) : rowData?.respondentName ? (
            rowData.respondentName
          ) : rowData?.respondentParties?.length ? (
            rowData?.respondentParties[0]?.name
          ) : (
            <div style={{ marginLeft: 35 }}>-</div>
          )}
        </div>
      ),
    },
    {
      field: "totalClaimValue",
      title: "Claim Value",
      tooltip: "Claim Value of the Case",
      render: (rowData) => {
        const caseItems = rowData.totalClaimValue;
        const divStyle = {
          cursor: "pointer",
        };
        if (caseItems)
          return (
            <div
              className="container"
              style={divStyle}
              onClick={() =>
                navigate(
                  `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
                )
              }
            >
              {numberFormat(
                parseFloat(caseItems).toFixed(2),
                rowData?.currencyUnit,
              )}
            </div>
          );
        else
          return (
            <div
              className="container"
              style={divStyle}
              onClick={() =>
                navigate(
                  `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
                )
              }
            >
              {"Non Monetary"}
            </div>
          );
      },
      sorting: true,
      headerStyle: {
        paddingLeft: "8px",
      },
    },
    {
      field: "nextHearingDate",
      title: labels["table.nextHearingDate"],
      render: (rowData) => (
        <div
          style={{ marginLeft: "10px", cursor: "pointer" }}
          onClick={() =>
            rowData?.resolutionKind !== "negotiation"
              ? getMeetingjoin(rowData.id)
              : null
          }
        >
          {rowData?.nextHearingDate?.date
            ? rowData?.nextHearingDate?.date
            : "-"}
        </div>
      ),
      sorting: true,
      headerStyle: {
        width: 110,
      },
      cellStyle: {
        textAlign: "center",
      },
    },
    {
      field: "status",
      title: "Status",
      tooltip: "Status of the Case",
      render: (rowData) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate(
              `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
            )
          }
        >
          {getCaseLabelByStatus(rowData)}
        </div>
      ),
    },
    {
      field: "loanAccountNo",
      title: "Contract Number",
      hidden: type === "arbitration" ? false : true,
      render: (rowData) =>
        rowData.loanAccountNo ? (
          <div
            onClick={() =>
              navigate(
                `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
              )
            }
          >
            {rowData.loanAccountNo}
          </div>
        ) : (
          <div style={{ marginLeft: "25px", cursor: "pointer" }}>{"-"}</div>
        ),
      sorting: false,
    },
    {
      field: "submittedOn",
      title: "Created",
      tooltip: "Case Created Date",
      headerStyle: {
        width: 120,
      },
      render: (rowData) => (
        <Datecolumn>
          <div
            className="date"
            onClick={() =>
              navigate(
                `/dashboard/case-manager/${rowData?.resolutionKind}/${rowData.id}`,
              )
            }
          >
            {rowData?.submittedOn
              ? moment(rowData.submittedOn).format("DD/MM/YYYY")
              : moment(parseTimeStamp(rowData.created_at)).format("DD/MM/YYYY")}
          </div>
          {rowData?.status === "draft" && (
            <div
              className="delete"
              onClick={() => deleteWaring(false, rowData?.id)}
            >
              {labels["table.delete"]}
            </div>
          )}
        </Datecolumn>
      ),
    },
  ];

  const detailPanel = [
    {
      tooltip: "View case details",
      render: (rowData) => (
        <CaseSplitUp data={rowData} type={type} userObject={userObject} />
      ),
    },
  ];

  const data = (query) =>
    new Promise((resolve, reject) => {
      let params = {
        page: query.page + 1,
        perPage: 10,
      };

      if (query.search) {
        params.search = query.search;
      }
      if (type) {
        params.resolutionKind = type;
      }
      params.selected = true;
      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }
      const { key, value } = filterByStatus(selectedFilter);
      if (key && value) {
        params[key] = value;
      }
      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }
      getCases(stringParams, type)
        .then(async (result) => {
          if (result) {
            resolve({
              data: result?.data?.map((row) => ({
                ...row,
                tableData: { checked: row?.isSelected },
              })),
              page: result.page - 1,
              total: result.total,
            });
            setState(result);
            setStats(result.stats);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });

  const handleCaseSelection = async (cases, data) => {
    const param = `?resolutionKind=${type}`;
    if (cases?.length >= 1 && !data) {
      try {
        setLoader({ state: true, message: "Modifying Cases..." });
        await CaseService.addAllCases(param);
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
        MTRef.current.onQueryChange("");
      }
    } else if (cases?.length === 0 && !data) {
      try {
        setLoader({ state: true, message: "Modifying Cases..." });
        await CaseService.removeAllCases(param);
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
        MTRef.current.onQueryChange("");
      }
    } else {
      try {
        setLoader({ state: true, message: "Modifying Cases..." });
        if (data?.isSelected) {
          await CaseService.removeCaseItem(data.id);
        } else {
          await CaseService.selectCaseItem(data?.id);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
        MTRef.current.onQueryChange("");
      }
    }
  };

  const deleteWaring = (isSelectAll, id) => {
    setOpen(true);
    setDialogData({
      primaryBtnText: "Proceed",
      secondaryBtnText: "Cancel",
      clickPrimaryBtn: () =>
        isSelectAll ? deleteSelectedDraft() : deleteDraft(id),
      clickSecondarybtn: () => setOpen(false),
      onDialogClose: () => setOpen(false),
      desc: `Are you sure you want to delete this draft case(s)?`,
      heading: "Delete Case",
      descriptionTextStyle: {
        margin: "0px 60px 50px",
        textAlign: "center",
        fontFamily: theme.fonts.primaryFontSemiBold,
      },
    });
  };

  const deleteSelectedDraft = async () => {
    try {
      setLoader({ state: true, message: "Deleting Cases..." });
      const res = await CaseService.deleteSelectedCases();
      if (res?.message) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
      setOpen(false);
    }
  };

  const deleteDraft = async (id) => {
    try {
      setLoader({ state: true, message: "Deleting Cases..." });
      const res = await CaseService.deleteDraftCase(id);
      if (res?.message) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
      setOpen(false);
    }
  };

  return (
    <div className="myCases">
      <ActionBar breadcrumbs={breadcrumbs} actions={actions} />
      <Padding>
        <CustomTable
          hidePagination={state.lastPage === 1}
          pageSize={state?.data?.length ? state?.data?.length : 10}
          pluralTitle="Cases"
          singularTitle=""
          selection={selectedFilter === "Drafts"}
          onSelectionChange={handleCaseSelection}
          onFilterClick={() => setModal(true)}
          {...{
            columns,
            data,
            filters,
            selectedFilter,
            setSelectedFilter,
            placeholderText,
            MTRef,
          }}
          detailPanel={detailPanel}
          state={state}
        />
        <Footer>
          <div className="flex mt20">
            <div
              style={{
                display:
                  selectedFilter === "Drafts" &&
                  state?.data?.some((el) => el.isSelected)
                    ? "flex"
                    : "none",
                flex: 1,
                justifyContent: "flex-end",
              }}
            >
              <DangerBtn
                style={{
                  width: "216px",
                  marginLeft: "26px",
                }}
                onClick={() => deleteWaring(true)}
              >
                {labels["table.delete"]}
              </DangerBtn>
            </div>
          </div>
        </Footer>
        <FilterModal {...{ modal, setModal }} />
        <AssignNeutralBulkCase {...{ drawer, setDrawer }} />
        <AssignCaseLifeCycleBulkCase
          {...{ lifecycleModal, setLifeCycleModal }}
        />
        {bulkUpload && (
          <BulkUpload
            {...{ bulkUpload, setBulkUpload, cmList }}
            cmId={userObject?.id}
          />
        )}
        <AlertDialog isOpen={open} {...{ ...dialogData }} />
      </Padding>
    </div>
  );
};

export default Index;

const Padding = styled.div`
  padding-top: 30px;
  padding-bottom: 50px;
  padding-left: 35px;
  padding-right: 53px;
`;

const HyperLink = styled.span`
  cursor: pointer;
  color: ${COLORS.BTN_GREEN};
  text-decoration: underline;
`;

const Bold = styled.span`
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const Datecolumn = styled.div`
  .date {
    cursor: pointer;
  }
  .delete {
    margin-top: 2px;
    cursor: pointer;
    color: ${COLORS.LOGOUT_RED};
    font-size: 12px;
  }
`;

export const Footer = styled.footer`
  & .highlighted {
    color: red;
    margin-top: 10px;
    font-size: 12px;
    font-family: ${theme.fonts.primaryFontSemiBold};
  }
`;

export const DangerBtn = styled(PrimaryCTAButton)`
  background-color: #ff3c3c;
  &:focus,
  &:hover {
    background-color: #ff4d4d;
    border: 1px solid #ff3c3c;
  }
  border: 1px solid #ff3c3c;
`;

export const buildStatusHistory = (
  caseStatusUpdatedTime,
  parseDate,
  userName,
) => {
  return Object.entries(caseStatusUpdatedTime || {})
    .map(([key, value]) => {
      const hearingDate = value?.date || null;
      const statusUpdateDate = value?.updatedDate || null;

      return {
        key,
        label: key.includes(" ") ? key : _.startCase(key),
        hearingDate,
        statusUpdateDate,
        rawDate: statusUpdateDate || hearingDate,
        parsedDate: parseDate(statusUpdateDate || hearingDate),
        name: value?.name || userName || null,
        remarks: value?.remarks || null,
      };
    })
    .sort((a, b) => {
      if (a.parsedDate && b.parsedDate) return a.parsedDate - b.parsedDate;
      if (a.parsedDate) return -1;
      if (b.parsedDate) return 1;
      return 0;
    });
};

const CaseSplitUp = ({ data, type, userObject }) => {
  const hearing = data?.nextHearingDate;

  const parseDate = (date) => {
    if (!date) return null;
    const parsed = moment(
      date,
      ["DD/MM/YYYY HH:mm:ss", "YYYY-MM-DD HH:mm:ss"],
      true,
    );
    return parsed.isValid() ? parsed : null;
  };

  const formatDateTime = (date) => {
    const parsed = parseDate(date);
    return parsed
      ? parsed.format("DD MMM YYYY, hh:mm A")
      : date || "Not available";
  };

  const statusHistory = buildStatusHistory(
    data?.caseStatusUpdatedTime,
    parseDate,
    userObject?.name,
  );

  const hearingCards = [
    { label: "Last updated at", value: formatDateTime(data?.updated_at) },
    {
      label: "Hearing date updated at",
      value: formatDateTime(data?.hearingDateUpdatedDate),
    },
    hearing?.hearingType && {
      label: "Hearing type",
      value: hearing.hearingType,
    },
    hearing?.remarks && { label: "Hearing remarks", value: hearing.remarks },
    data?.counterClaimDeadLine && {
      label: "Counterclaim deadline",
      value: moment(data.counterClaimDeadLine).format("DD MMM YYYY"),
    },
  ].filter(Boolean);

  return (
    <CaseDetailsPanel>
      <CaseDetailsHeader>
        <div>
          <CaseDetailsEyebrow>Additional case details</CaseDetailsEyebrow>
          <CaseDetailsTitle>Hearing information & status</CaseDetailsTitle>
        </div>
      </CaseDetailsHeader>

      <CaseDetailsSections>
        <DetailSection>
          <SectionHeading>Hearing & case progress</SectionHeading>
          <SectionSubheading>
            Upcoming hearing and action-related information
          </SectionSubheading>
          <InfoGrid>
            {hearingCards.map((card, idx) => (
              <InfoCard key={`${card.label}-${idx}`}>
                <InfoCardLabel>{card.label}</InfoCardLabel>
                <InfoCardValue>{card.value}</InfoCardValue>
              </InfoCard>
            ))}
          </InfoGrid>
        </DetailSection>

        {statusHistory.length > 0 && (
          <DetailSection>
            <SectionHeading>Case status history</SectionHeading>
            <SectionSubheading>
              {`${statusHistory.length} status change${statusHistory.length > 1 ? "s" : ""} recorded for this case`}
            </SectionSubheading>
            <StatusHistoryList>
              {statusHistory.map((entry, idx) => {
                const color = getStatusColor(entry.key);
                return (
                  <StatusHistoryCard
                    key={`${entry.key}-${idx}`}
                    style={{
                      background: color.bg,
                      borderLeft: `4px solid ${color.accent}`,
                    }}
                  >
                    <StatusHistoryTop>
                      <StatusHistoryHeader>
                        <StatusHistoryLabel style={{ color: color.text }}>
                          {entry.label}
                        </StatusHistoryLabel>
                        <StatusHistoryBadge
                          style={{ background: color.accent }}
                        >
                          {idx + 1}
                        </StatusHistoryBadge>
                      </StatusHistoryHeader>

                      {(entry.hearingDate || entry.statusUpdateDate) && (
                        <StatusHistoryDates>
                          {entry.hearingDate && (
                            <StatusHistoryMetaRow>
                              <span>Status Date</span>
                              <strong>
                                {formatDateTime(entry.hearingDate)}
                              </strong>
                            </StatusHistoryMetaRow>
                          )}
                          {entry.statusUpdateDate && (
                            <StatusHistoryMetaRow>
                              <span>Updated At</span>
                              <strong>
                                {formatDateTime(entry.statusUpdateDate)}
                              </strong>
                            </StatusHistoryMetaRow>
                          )}
                        </StatusHistoryDates>
                      )}
                    </StatusHistoryTop>

                    {(entry.name || entry.remarks) && (
                      <StatusHistoryMeta>
                        {entry.name && (
                          <StatusHistoryMetaRow>
                            <span>By</span>
                            <strong>{entry.name}</strong>
                          </StatusHistoryMetaRow>
                        )}
                        {entry.remarks && (
                          <StatusHistoryMetaRow>
                            <span>Remarks</span>
                            <strong>{entry.remarks}</strong>
                          </StatusHistoryMetaRow>
                        )}
                      </StatusHistoryMeta>
                    )}
                  </StatusHistoryCard>
                );
              })}
            </StatusHistoryList>
          </DetailSection>
        )}
      </CaseDetailsSections>

      {data?.description && (
        <DescriptionCard>
          <DetailLabel>Description</DetailLabel>
          <DescriptionText>{data.description}</DescriptionText>
        </DescriptionCard>
      )}
    </CaseDetailsPanel>
  );
};

const CaseDetailsPanel = styled.div`
  padding: 24px 28px 28px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  color: ${COLORS.COLOR_DARK};

  @media (max-width: 767px) {
    padding: 20px 16px;
  }
`;

const CaseDetailsHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const CaseDetailsEyebrow = styled.div`
  color: #64748b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CaseDetailsTitle = styled.div`
  margin-top: 4px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 17px;
  line-height: 1.4;
`;

const DetailLabel = styled.div`
  color: ${COLORS.INPUT_LABEL};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const CaseDetailsSections = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DetailSection = styled.div`
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
`;

const SectionHeading = styled.div`
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
`;

const SectionSubheading = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
`;

/* ---------- Hearing & case progress ---------- */

const InfoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;

const InfoCard = styled.div`
  flex: 1 1 140px;
  min-width: 140px;
  max-width: 220px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e8eef4;
  background: #fbfdff;
`;

const InfoCardLabel = styled.div`
  color: ${COLORS.INPUT_LABEL};
  font-size: 11px;
  line-height: 1.3;
`;

const InfoCardValue = styled.div`
  margin-top: 4px;
  overflow-wrap: anywhere;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  line-height: 1.35;
`;

/* ---------- Case status history ---------- */

const StatusHistoryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-auto-rows: 1fr;
  gap: 10px;
  margin-top: 14px;
`;

const StatusHistoryCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e8eef4;
  min-width: 0;
`;

const StatusHistoryTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatusHistoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const StatusHistoryLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12.5px;
  line-height: 1.3;
`;

const StatusHistoryBadge = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #ffffff;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  line-height: 1;
`;

const StatusHistoryDates = styled.div`
  display: grid;
  gap: 2px;
`;

const StatusHistoryMeta = styled.div`
  display: grid;
  gap: 3px;
  padding-top: 8px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
`;

const StatusHistoryMetaRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  color: ${COLORS.INPUT_LABEL};
  font-size: 11.5px;

  strong {
    overflow-wrap: anywhere;
    color: ${COLORS.COLOR_DARK};
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-weight: normal;
    text-align: right;
  }
`;

const DescriptionCard = styled.div`
  margin-top: 12px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
`;

const DescriptionText = styled.div`
  margin-top: 8px;
  color: ${COLORS.COLOR_DARK};
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Container = styled.div`
  & .agent_row {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 3px 0px;
    & .agent_name {
      font-size: 16px;
      font-weight: 600;
      flex: 1;
      color: ${COLORS.COLOR_DARK};
      font-family: ${theme.fonts.primaryFontSemiBold};
    }
    & .agent_email {
      font-size: 16px;
      font-weight: 600;
      flex: 1;
      color: ${COLORS.TRANSCRIPT_GRAY};
      font-family: ${theme.fonts.primaryFontSemiBold};
    }
    & .add_circle {
      margin: 0px 20px;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(0, 131, 140, 0.13);
      cursor: pointer;
      & .add_icon {
        font-size: 14px;
        color: ${COLORS.BTN_GREEN};
      }
    }
  }
`;
